/**
 * ADR-0183 §11 — proposal detail (`/app/optimize/proposals/:id`).
 *
 * The priced answer first (hours saved, promises at risk, moves), then the
 * per-machine run order, then the receipt — solver status, wall time, per-input
 * freshness, and the snapshots rendered as sentences, never raw JSON.
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { LoadingState, ErrorState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { formatTimestamp, formatTimestampOr } from "../lib/formatTime.js";

type SequenceJob = {
  job: string;
  part: string;
  start: string;
  end: string;
  setup_class: string | null;
  setup_hours: number;
  setup_cost_fallback?: boolean;
  pinned: boolean;
};

type Proposal = {
  id: string;
  model_id: string;
  version: number;
  kind: "solve" | "what_if";
  result: { sequences?: Array<{ machine: string; jobs: SequenceJob[] }> } | null;
  diff: {
    setup_hours_proposed?: number;
    setup_hours_current?: number;
    hours_saved?: number;
    promises_at_risk?: Array<{ job: string; part: string; machine: string; due: string; projected_end: string }>;
    moves_from_standing?: number;
    cost_coverage?: { fallback_transitions?: number; uncovered_patterns?: unknown[] } | null;
  } | null;
  promoted_from_proposal_id: string | null;
  created_at: string;
};

type Solve = {
  id: string;
  trigger: string;
  refresh_inputs: boolean;
  solver_status: "optimal" | "feasible" | "infeasible" | null;
  wall_ms: number | null;
  optimality_gap: number | null;
  overlay_snapshot: Array<{ kind: string; subject: Record<string, unknown> }>;
  pins_snapshot: string[];
  weights_snapshot: Record<string, number>;
  inputs_meta: Record<
    string,
    { operation_name?: string; row_count?: number; last_run_at?: string | null; resolved_from?: string }
  > | null;
  model_snapshot: { id: string; name?: string } | null;
  template_snapshot: {
    id: string;
    version: number;
    body?: {
      work?: { slot: string; label: string };
      blocks?: Array<{ kind: string; reads?: { slot: string; label: string } }>;
      objective?: { weights?: Array<{ key: string; label: string }> };
    };
  } | null;
  queued_at: string;
  finished_at: string | null;
};

export function OptimizeProposal() {
  const { proposalId = "" } = useParams();
  const nav = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [solve, setSolve] = useState<Solve | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ proposal: Proposal; solve: Solve | null }>(
        `/api/tenant/optimize/proposals/${proposalId}`
      );
      setProposal(data.proposal);
      setSolve(data.solve);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [proposalId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function promote() {
    if (!proposal) return;
    const ok = await confirm.confirm({
      title: "Promote this what-if to standing?",
      detail:
        "A new standing-class version is created from exactly what you reviewed — no re-solve. The report and future diffs price themselves against it.",
      confirmLabel: "Promote",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const promoted = await apiFetch<{ proposal: { id: string } }>(
        `/api/tenant/optimize/proposals/${proposalId}/promote`,
        { method: "POST" }
      );
      toast.push({ kind: "success", message: "Promoted to standing." });
      nav(`/app/optimize/proposals/${promoted.proposal.id}`);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
      setBusy(false);
    }
  }

  if (error) return <PageLayout title="Proposal"><ErrorState detail={error} /></PageLayout>;
  if (loading || !proposal) return <PageLayout title="Proposal"><LoadingState /></PageLayout>;

  const diff = proposal.diff ?? {};
  const atRisk = diff.promises_at_risk ?? [];
  const fallbackCount = diff.cost_coverage?.fallback_transitions ?? 0;
  const sequences = proposal.result?.sequences ?? [];
  const overlayCounts = new Map<string, number>();
  for (const o of solve?.overlay_snapshot ?? []) {
    overlayCounts.set(o.kind, (overlayCounts.get(o.kind) ?? 0) + 1);
  }
  const overlaySentence =
    (solve?.overlay_snapshot?.length ?? 0) === 0
      ? "No disruptions were marked."
      : [...overlayCounts.entries()]
          .map(([kind, n]) => `${n} ${kind.replace(/_/g, " ")}${n > 1 ? "s" : ""}`)
          .join(", ") + ((solve?.overlay_snapshot?.length ?? 0) > 1 ? " were in effect." : " was in effect.");
  const pinSentence =
    (solve?.pins_snapshot?.length ?? 0) === 0
      ? "No jobs were pinned beyond the running work."
      : `${solve!.pins_snapshot.length} job${solve!.pins_snapshot.length > 1 ? "s were" : " was"} pinned.`;
  const knobLabels = new Map(
    (solve?.template_snapshot?.body?.objective?.weights ?? []).map((w) => [w.key, w.label])
  );
  const weightSentence = solve
    ? Object.entries(solve.weights_snapshot)
        .filter(([k]) => typeof solve.weights_snapshot[k] === "number")
        .map(([k, v]) => (k === "hard_promises" ? `hard promises ${v ? "on" : "off"}` : `${knobLabels.get(k) ?? k.replace(/_/g, " ")} ${v}`))
        .join(", ")
    : "";

  return (
    <PageLayout
      title={
        <span className="breadcrumbTitle">
          <Link to="/app/optimize" style={{ color: "inherit", textDecoration: "none", opacity: 0.6 }}>
            Optimize
          </Link>
          <span style={{ opacity: 0.4, margin: "0 6px" }}>/</span>
          <Link to={`/app/optimize/${proposal.model_id}`} style={{ color: "inherit", textDecoration: "none", opacity: 0.6 }}>
            {solve?.model_snapshot?.name ?? "Model"}
          </Link>
          <span style={{ opacity: 0.4, margin: "0 6px" }}>/</span>
          <span>
            Proposal v{proposal.version} {proposal.kind === "what_if" ? "(what-if)" : ""}
          </span>
        </span>
      }
      actions={
        proposal.kind === "what_if" ? (
          <button className="btn btnPrimary" onClick={() => void promote()} disabled={busy}>
            {busy ? "Promoting…" : "Promote to standing"}
          </button>
        ) : undefined
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        {/* ── The priced diff, first ─────────────────────────────────────── */}
        <div className="card">
          <strong>What this plan is worth</strong>
          <div style={{ display: "flex", gap: 24, marginTop: 8, flexWrap: "wrap" }}>
            <div>
              <div className="muted" style={{ fontSize: 12 }}>Setup hours saved</div>
              <div style={{ fontSize: 22, fontWeight: 600 }}>
                {diff.hours_saved != null ? `${diff.hours_saved} h` : "—"}
              </div>
              <div className="help">
                {diff.setup_hours_proposed != null && diff.setup_hours_current != null
                  ? `${diff.setup_hours_proposed} h proposed vs ${diff.setup_hours_current} h in the current order`
                  : ""}
              </div>
            </div>
            <div>
              <div className="muted" style={{ fontSize: 12 }}>Promises at risk</div>
              <div style={{ fontSize: 22, fontWeight: 600 }}>{atRisk.length}</div>
            </div>
            <div>
              <div className="muted" style={{ fontSize: 12 }}>Moves from the standing order</div>
              <div style={{ fontSize: 22, fontWeight: 600 }}>{diff.moves_from_standing ?? "—"}</div>
            </div>
          </div>
          {fallbackCount > 0 ? (
            <div className="help" style={{ color: "var(--danger-text)", marginTop: 8 }}>
              {fallbackCount} changeover{fallbackCount > 1 ? "s" : ""} in this plan had no mapped cost class and
              {fallbackCount > 1 ? " were" : " was"} priced at the full-changeover class — complete the cost-class
              mapping on the model to price {fallbackCount > 1 ? "them" : "it"} from measured history.
            </div>
          ) : null}
          {atRisk.length > 0 ? (
            <div className="tableWrap" style={{ marginTop: 10 }}>
              <table className="table tableCompact">
                <thead>
                  <tr>
                    <th>Job</th>
                    <th>Part</th>
                    <th>Machine</th>
                    <th>Promised</th>
                    <th>Projected finish</th>
                  </tr>
                </thead>
                <tbody>
                  {atRisk.map((r) => (
                    <tr key={r.job}>
                      <td className="mono">{r.job}</td>
                      <td>{r.part}</td>
                      <td>{r.machine}</td>
                      <td>{formatTimestamp(r.due)}</td>
                      <td>{formatTimestamp(r.projected_end)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>

        {/* ── Per-machine run order ──────────────────────────────────────── */}
        {sequences.map((seq) => (
          <div className="card adminList" key={seq.machine}>
            <strong>{seq.machine}</strong>
            <div className="tableWrap">
              <table className="table tableCompact">
                <thead>
                  <tr>
                    <th>Job</th>
                    <th>Part</th>
                    <th>Start</th>
                    <th>Finish</th>
                    <th>Changeover</th>
                    <th>Setup hours</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {seq.jobs.map((j) => (
                    <tr key={j.job}>
                      <td className="mono">{j.job}</td>
                      <td>{j.part}</td>
                      <td>{formatTimestamp(j.start)}</td>
                      <td>{formatTimestamp(j.end)}</td>
                      <td>{j.setup_class ?? "first on machine"}</td>
                      <td>{j.setup_class ? j.setup_hours : ""}</td>
                      <td>
                        {j.pinned ? <span className="pill pillNeutral">Pinned</span> : null}
                        {j.setup_cost_fallback ? (
                          <span
                            className="pill pillWarn"
                            title="This changeover's pattern has no mapped cost class — priced at the full-changeover class."
                          >
                            Estimated cost
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        {/* ── The receipt ────────────────────────────────────────────────── */}
        <div className="card">
          <strong>How this was solved</strong>
          <div style={{ display: "grid", gap: 6, marginTop: 8 }}>
            <div>
              {solve?.solver_status === "optimal"
                ? "The engine proved this plan optimal"
                : solve?.solver_status === "feasible"
                  ? "The engine found this plan within its time budget (a better one may exist)"
                  : "Solver result unavailable"}
              {solve?.wall_ms != null ? ` in ${(solve.wall_ms / 1000).toFixed(1)} seconds.` : "."}
              {solve?.optimality_gap != null && solve.solver_status === "feasible"
                ? ` Remaining optimality gap ${(solve.optimality_gap * 100).toFixed(1)}%.`
                : ""}
            </div>
            <div>{overlaySentence} {pinSentence}</div>
            {weightSentence ? <div>Weights: {weightSentence}.</div> : null}
            {solve?.refresh_inputs ? <div>Inputs were refreshed from the source systems before solving.</div> : null}
            {proposal.promoted_from_proposal_id ? (
              <div>
                Promoted from{" "}
                <Link to={`/app/optimize/proposals/${proposal.promoted_from_proposal_id}`}>a reviewed what-if</Link> — no
                re-solve.
              </div>
            ) : null}
            <div className="muted">Proposed {formatTimestamp(proposal.created_at)}.</div>
          </div>
          {solve?.inputs_meta ? (
            <div className="tableWrap" style={{ marginTop: 10 }}>
              <table className="table tableCompact">
                <thead>
                  <tr>
                    <th>Input</th>
                    <th>From</th>
                    <th>Rows</th>
                    <th>Data as of</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(solve.inputs_meta).map(([slotName, meta]) => {
                    // Label each input from the template snapshot's declared
                    // reads: the work, or a block's read carrying this slot.
                    const body = solve.template_snapshot?.body;
                    const label =
                      body?.work?.slot === slotName
                        ? body.work.label
                        : body?.blocks?.find((b) => b.reads?.slot === slotName)?.reads?.label;
                    return (
                    <tr key={slotName}>
                      <td>{label ?? slotName.replace(/_/g, " ")}</td>
                      <td>{meta.operation_name ?? ""}</td>
                      <td>{meta.row_count ?? ""}</td>
                      <td>{formatTimestampOr(meta.last_run_at ?? null, "unknown")}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>
    </PageLayout>
  );
}
